import {
	Box,
	ButtonBase,
	LinearProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	GOAL_CATEGORY_CONFIG,
	type Goal,
	STATUS_LABEL_MAP,
	STATUS_STYLE_CONFIG,
} from "web_myplann/Goals/Types/GoalTypes";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	goal: Goal;
	onClick: (goalId: string) => void;
};

export const GoalListItem: FC<Props> = ({ goal, onClick }) => {
	const theme = useTheme();
	const { t } = useResource();

	const formatAmount = useFormatGoalAmount();

	const styleConfig = STATUS_STYLE_CONFIG[goal.status];
	const statusLabel = t(STATUS_LABEL_MAP[goal.status]);
	const categoryConfig = GOAL_CATEGORY_CONFIG[goal.category];
	const CategoryIcon = categoryConfig.icon;
	const covered = goal.contracts.reduce((sum, c) => sum + c.value, 0);
	const progress =
		goal.totalValue > 0
			? Math.min(100, Math.round((covered / goal.totalValue) * 100))
			: 0;

	return (
		<ButtonBase
			onClick={() => onClick(goal.id)}
			sx={{ width: "100%", textAlign: "left" }}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={1.2}
				px={1.6}
				py={1.2}
				width="100%"
			>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: "12px",
						bgcolor: categoryConfig.bgColor,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<CategoryIcon size={20} color={categoryConfig.color} />
				</Box>

				<Stack flex={1} minWidth={0}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						mb={0.4}
					>
						<Typography
							fontSize={14}
							fontWeight={600}
							color={theme.palette.grey[900]}
						>
							{goal.name}
						</Typography>
						<Typography
							fontSize={12}
							fontWeight={500}
							color={styleConfig.color}
						>
							{statusLabel}
						</Typography>
					</Stack>

					<LinearProgress
						variant="determinate"
						value={progress}
						sx={{
							height: 6,
							borderRadius: 3,
							bgcolor: theme.palette.grey[100],
							mb: 0.4,
							"& .MuiLinearProgress-bar": {
								borderRadius: 3,
								bgcolor: styleConfig.progressColor,
							},
						}}
					/>

					<Typography
						fontSize={12}
						color={theme.palette.grey[500]}
						sx={{ fontVariantNumeric: "tabular-nums" }}
					>
						{formatAmount(covered)} / {formatAmount(goal.totalValue)}{" "}
						{t(Resources.Goals.Amount.currencySign)} · {goal.dueIn}
					</Typography>
				</Stack>

				<ChevronRight size={16} color={theme.palette.grey[400]} />
			</Stack>
		</ButtonBase>
	);
};
